import React from 'react';
import { Check } from 'lucide-react';
import { Reveal, SectionLabel, BtnRed, BtnGhost, CtaBanner } from '../components/common';
import { images, serviceBlocks, clientGains } from '../mock/mock';

export default function HazloPosible() {
  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <img src={images.prod1} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b0b0d]/85 via-[#0b0b0d]/70 to-[#0b0b0d]" />
        <div className="relative max-w-[1280px] mx-auto px-5 md:px-8 pt-28 w-full">
          <Reveal><SectionLabel light>De la idea al momento en que todo empieza</SectionLabel></Reveal>
          <Reveal delay={100}>
            <h1 className="font-display font-bold uppercase text-4xl md:text-6xl leading-[1.02] text-white max-w-3xl">
              Una buena idea no basta.
              <span className="block text-[var(--mt-red)]">Tiene que poder suceder.</span>
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-7 max-w-xl text-white/70 leading-relaxed">
              MusicTeam convierte una intención en una experiencia real. Diseñamos la propuesta, reunimos a los
              profesionales adecuados y coordinamos cada pieza.
            </p>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <BtnRed to="/hablemos">Cuéntanos qué tienes en mente</BtnRed>
              <BtnGhost to="/en-movimiento">Así hacemos que suceda</BtnGhost>
            </div>
          </Reveal>
        </div>
      </section>

      {/* POSICIONAMIENTO */}
      <section className="py-24 md:py-28 mt-checker">
        <div className="max-w-[1000px] mx-auto px-5 md:px-8">
          <Reveal><SectionLabel>Creatividad con los pies en el escenario</SectionLabel></Reveal>
          <Reveal delay={100}>
            <h2 className="font-display font-bold uppercase text-2xl md:text-4xl text-white leading-tight">
              Lo que emociona delante necesita mucho trabajo detrás.
            </h2>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-6 text-white/60 leading-relaxed max-w-2xl">
              El público ve una experiencia fluida. Pero antes ha habido decisiones, horarios, artistas, técnicos,
              accesos, sonido, montaje y coordinación. Nuestro trabajo consiste en conectar todo eso sin perder de
              vista lo más importante: lo que queremos que ocurra en el espacio.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SERVICE BLOCKS (steps) */}
      <section className="bg-[var(--mt-bg-alt)] py-24 md:py-32">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <div className="space-y-0">
            {serviceBlocks.map((b, i) => (
              <Reveal key={b.n}>
                <div className="grid md:grid-cols-12 gap-6 md:gap-10 py-10 border-t border-white/10 items-start">
                  <div className="md:col-span-3 flex items-baseline gap-4">
                    <span className="mt-num text-6xl md:text-7xl">{b.n}</span>
                  </div>
                  <div className="md:col-span-9">
                    <span className="font-display uppercase tracking-[0.25em] text-xs text-[var(--mt-red)]">{b.sub}</span>
                    <h3 className="font-display font-bold uppercase text-2xl md:text-3xl text-white mt-2">{b.title}</h3>
                    <p className="mt-4 text-white/60 leading-relaxed max-w-2xl">{b.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTION IMAGE SPLIT */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <Reveal>
            <div className="relative">
              <img src={images.prod2} alt="Producción técnica" className="w-full h-[420px] object-cover grayscale brightness-90" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-[var(--mt-red)]" />
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div>
              <SectionLabel>Todo lo que deja de tener que coordinar</SectionLabel>
              <h2 className="font-display font-bold uppercase text-2xl md:text-4xl text-white leading-tight">
                Un único equipo. Una visión completa. Más tranquilidad.
              </h2>
              <ul className="mt-7 space-y-3">
                {clientGains.map((g) => (
                  <li key={g} className="flex items-start gap-3 text-white/65">
                    <Check size={18} className="text-[var(--mt-red)] mt-0.5 shrink-0" />
                    {g}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBanner
        label="Hazlo posible"
        title="Cuéntanos qué quieres que suceda."
        redTitle="Nosotros conectaremos las piezas."
        text="No hace falta llegar con una propuesta cerrada. Puede ser una idea, una necesidad, un espacio o un objetivo."
        cta="Empecemos por la idea"
        image={images.stageRed}
      />
    </div>
  );
}
